import { z } from "zod";

// Suggestion #12 closure rationale (REVIEWS MEDIUM):
// Plan 03 ships a declarative field-descriptor pattern for list and nested-picker sub-blocks.
// The declarative approach — `fields: ItemField[]` — replaces the prior example-item-shape
// approach that used a single example-item to convey shape. Each field is now described
// explicitly with name, type, and required flag, giving the engine a machine-readable
// contract to validate and document per-block data layout.

// REVIEWS-3 LOW: ItemField.type uses "string" for amounts because recipe-author
// amounts are free-text (e.g., "1/2 of a unit", "to taste"). Representing amounts as plain strings
// faithfully preserves the author's intent without forcing a numeric parse. A future "unit"
// type may be added in v2 if structured amount parsing is needed.

// REVIEWS-3 LOW (MEDIUM #3): template-driven block configs are intentionally
// `config: z.unknown()` in Phase 01.1. Plan 06 validators.ts only cross-checks `templateId`
// against TEMPLATE_CATALOG; Phase 6 / Phase 7 / Phase 11 land per-template configSchemas
// that validate the config shape itself. Until then, AI-extracted recipes can produce
// malformed template-driven configs that pass validateRecipe but fail at render time.
// This is an accepted transitional state per CONTEXT D-05 + REVIEWS Consensus #7.

// REVIEWS-3 LOW (MEDIUM #4): Pinned to Zod 4.4.3 discriminatedUnion behavior:
// the discriminator is a `z.literal(...)` per variant. Verify behavior before upgrading
// Zod — Zod 5+ may change discriminator inference rules.

// NOTE: z.discriminatedUnion works here because all four variants are plain
// z.strictObject() schemas sharing z.literal("kind") as the discriminator.
// See Plan 04 constraint.ts for the inverse case where z.union is required
// because variants are themselves unions (LeafPredicateSchema). If you add
// a new OptionCategoryBlock variant, keep it as a plain z.strictObject().

// isolatedDeclarations + verbatimModuleSyntax: explicit interfaces for output types,
// z.ZodType<T> annotations for exported consts. No re-export type alias needed.

// ---------------------------------------------------------------------------
// ItemField: declarative descriptor for a single field in a list or nested-picker block.
// ---------------------------------------------------------------------------

export interface ItemField {
  name: string; // kebab-case field identifier (e.g., "name", "amount", "note")
  type: "string" | "number"; // v1 supports string + number (see comment above on amounts)
  required: boolean;
}

const _itemFieldSchema: z.ZodType<ItemField> = z.strictObject({
  name: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case field name required"),
  type: z.enum(["string", "number"]),
  required: z.boolean(),
});

// Duplicate-name guard: fields within a single block must have unique names.
const _itemFieldArraySchema = z
  .array(_itemFieldSchema)
  .min(1)
  .max(10)
  .superRefine((arr, ctx) => {
    const names = arr.map((f) => f.name);
    const dups = names.filter((n, i) => names.indexOf(n) !== i);
    if (dups.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Duplicate field names: ${dups.join(", ")}`,
      });
    }
  });

// ---------------------------------------------------------------------------
// Four OptionCategoryBlock variants — each is a plain z.strictObject() so that
// z.discriminatedUnion("kind", [...]) can dispatch correctly (see file header).
// ---------------------------------------------------------------------------

// single-value: one scalar attribute (string or number) for the entire option.
// Example use: a single-attribute block where each option picks exactly one value
// from a recipe-declared set (the attribute name comes from recipe DATA).
const _singleValueBlockSchema = z.strictObject({
  kind: z.literal("single-value"),
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(100),
  valueType: z.enum(["string", "number"]),
  optional: z.boolean().default(false),
});

// list: an ordered collection of items described by a declarative fields descriptor.
// Example use: a "spices" block where each flavor carries a list of {name, amount} entries. // grep-allow: spices, spice
const _listBlockSchema = z.strictObject({
  kind: z.literal("list"),
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(100),
  fields: _itemFieldArraySchema,
});

// nested-picker: a bounded sub-selection from the list items, constrained by pickCount.
// Example use: "plus ONE of these spices" — min:0, max:1 selection from the spices list. // grep-allow: spices, spice
const _nestedPickerBlockSchema = z.strictObject({
  kind: z.literal("nested-picker"),
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(100),
  pickCount: z.strictObject({
    min: z.number().int().nonnegative(),
    max: z.number().int().positive(),
  }),
  fields: _itemFieldArraySchema,
});

// template-driven: delegates rendering to a named template. config is z.unknown() in v1.
// REVIEWS-3 LOW (MEDIUM #3): config is z.unknown() in Phase 01.1.
// Plan 06 cross-checks templateId via TEMPLATE_CATALOG. Phase 6/7/11 lands per-template
// configSchemas that validate the config shape itself.
const _templateDrivenBlockSchema = z.strictObject({
  kind: z.literal("template-driven"),
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  templateId: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case templateId required"),
  config: z.unknown(),
});

// ---------------------------------------------------------------------------
// Discriminated union — O(1) dispatch on 'kind'.
// Zod 4.4.3: z.discriminatedUnion accepts z.literal() as the discriminator key.
// ---------------------------------------------------------------------------

const _optionCategoryBlockSchema = z.discriminatedUnion("kind", [
  _singleValueBlockSchema,
  _listBlockSchema,
  _nestedPickerBlockSchema,
  _templateDrivenBlockSchema,
]);

// Explicit output type for isolatedDeclarations + exactOptionalPropertyTypes compatibility.
export type OptionCategoryBlock =
  | {
      kind: "single-value";
      id: string;
      label: string;
      valueType: "string" | "number";
      optional: boolean;
    }
  | {
      kind: "list";
      id: string;
      label: string;
      fields: readonly ItemField[];
    }
  | {
      kind: "nested-picker";
      id: string;
      label: string;
      pickCount: { min: number; max: number };
      fields: readonly ItemField[];
    }
  | {
      kind: "template-driven";
      id: string;
      templateId: string;
      config: unknown;
    };

export const OptionCategoryBlock: z.ZodType<OptionCategoryBlock> =
  _optionCategoryBlockSchema as z.ZodType<OptionCategoryBlock>;

// ---------------------------------------------------------------------------
// OptionCategory: flat container with id, label, and an ordered list of blocks.
// D-16: categories are flat — no category nests another category.
// D-17: internal block structure within a category (not nested categories).
// D-18: pancake-domain names (spices, fat, etc.) live in fixtures, not here. // grep-allow: pancake, spices, spice, fat
// D-19: the engine knows zero category names — all names are recipe-declared.
// ---------------------------------------------------------------------------

export interface OptionCategory {
  id: string;
  label: string;
  blocks: readonly OptionCategoryBlock[];
}

export const OptionCategory: z.ZodType<OptionCategory> = z
  .strictObject({
    id: z
      .string()
      .min(1)
      .max(100)
      .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
    label: z.string().min(1).max(100),
    blocks: z.array(OptionCategoryBlock).min(1).max(20),
  })
  // Duplicate-block-id guard: block ids must be unique within a category. Two
  // blocks sharing an id collide into one composite line id downstream (the
  // per-option-detail-card renderer keys checkbox state + the Radix Checkbox
  // id/<label htmlFor> on `${optionId}::${sectionId}::${blockId}`), so one
  // checkbox would toggle both and the DOM would emit a duplicate id. Reject at
  // the validation boundary. Mirrors the field-name dedupe guard above and the
  // duplicate-row/option-id superRefine in option-tier-matrix.ts.
  .superRefine((category, ctx) => {
    const ids = category.blocks.map((b) => b.id);
    const dups = ids.filter((id, i) => ids.indexOf(id) !== i);
    if (dups.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `Duplicate block ids: ${dups.join(", ")}`,
      });
    }
  });
